const TYPE_PRIORITY = { place: 0, locality: 1, neighborhood: 2, address: 3 };

const BBOXES = [
    '-91,24,-79,37',   // Southeast: FL, AL, GA, MS, TN
    '-126,31,-102,49', // West: CA, OR, WA, UT, NV, AZ, ID, CO, WY, NM
];

// Core markets + bordering states (~150mi buffer)
const ALLOWED_STATES = new Set([
    'US-FL', 'US-AL', 'US-CA', 'US-OR', 'US-UT',
    'US-GA', 'US-MS', 'US-TN',
    'US-NV', 'US-AZ', 'US-WA', 'US-ID', 'US-CO', 'US-WY', 'US-NM',
]);

async function fetchBbox(url, bbox, signal) {
    const response = await fetch(`${url}&bbox=${bbox}`, { signal });
    if (!response.ok) return [];
    const data = await response.json();
    return data.features || [];
}

/**
 * Fetch geocoding suggestions from Mapbox Geocoding API v5.
 * Sends one request per region bbox, merges, deduplicates,
 * and filters to allowed US states.
 *
 * @param {string} query - Search text (>= 2 chars recommended)
 * @param {AbortSignal} [signal] - Optional AbortSignal for cancellation
 * @returns {Promise<Array<{ name: string, fullName: string, lat: number, lng: number, placeType: string }>>}
 */
export async function fetchGeocodingSuggestions(query, signal) {
    const token = import.meta.env.VITE_MAPBOX_TOKEN || '';
    if (!token || !query || query.length < 2) return [];

    const encoded = encodeURIComponent(query.trim());
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${token}&country=us&types=place,locality,neighborhood&limit=5&autocomplete=true`;

    const arrays = await Promise.all(
        BBOXES.map((bbox) => fetchBbox(url, bbox, signal)),
    );

    // Merge + deduplicate by id
    const seen = new Set();
    const features = arrays.flat().filter((f) => {
        if (seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
    });
    if (!features.length) return [];

    return features
        .filter((f) => {
            const region = f.context?.find((c) => c.id.startsWith('region.'));
            const code = region?.short_code?.toUpperCase();
            return code && ALLOWED_STATES.has(code);
        })
        .map((feature) => ({
            name: feature.text,
            fullName: feature.place_name.replace(/,\s*United States$/, ''),
            lat: feature.center[1],
            lng: feature.center[0],
            placeType: feature.place_type?.[0] || 'place',
        }))
        .sort((a, b) => (TYPE_PRIORITY[a.placeType] ?? 5) - (TYPE_PRIORITY[b.placeType] ?? 5));
}
